import React, { useState } from 'react';
import { List, Datagrid, TextField, BooleanField, DeleteButton, ResourceContextProvider,
  TopToolbar, Button, FormWithRedirect, TextInput, SelectInput, NumberInput, BooleanInput,
  CheckboxGroupInput, RadioButtonGroupInput, FormDataConsumer, SaveButton, useDataProvider, useNotify, useRefresh, Pagination } from 'react-admin';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Grid, Divider } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslate } from '../common/language';

const types = ['BUY_X_PAY_Y', 'PERCENTAGE', 'SECOND_UNIT_PERCENTAGE'];
const applicationTypes = ['FARES', 'BASKET', 'CODE'];
export const validateDiscount = values => {
  const errors = {};
  if (values.priority != null && (!Number.isInteger(values.priority) || values.priority < -2147483648 || values.priority > 2147483647)) errors.priority = 'tickets_discounts.invalid_priority';
  if (!values.name || !values.name.trim() || values.name.length > 255) errors.name = 'tickets_discounts.invalid_name';
  if (!types.includes(values.type)) errors.type = 'tickets_discounts.invalid_type';
  if (!applicationTypes.includes(values.application_type)) errors.application_type = 'tickets_discounts.invalid_application_type';
  if (values.application_type === 'CODE' && (!values.discount_code || !values.discount_code.trim() || values.discount_code.length > 20))
    errors.discount_code = 'tickets_discounts.invalid_discount_code';
  if (values.type === 'BUY_X_PAY_Y') {
    if (!Number.isInteger(values.quantity_x) || values.quantity_x <= 0) errors.quantity_x = 'tickets_discounts.invalid_quantities';
    if (!Number.isInteger(values.quantity_y) || values.quantity_y < 0 || values.quantity_x <= values.quantity_y)
      errors.quantity_y = 'tickets_discounts.invalid_quantities';
  } else if (types.includes(values.type) && (typeof values.percentage !== 'number' || !Number.isFinite(values.percentage) || values.percentage < 0 || values.percentage > 100)) {
    errors.percentage = 'tickets_discounts.invalid_percentage';
  }
  return errors;
};

const DiscountType = ({ record }) => {
  const translate = useTranslate();
  return <span>{record && translate('tickets_discounts.' + record.type)}</span>;
};
const DiscountValue = ({ record }) => <span>{record && (record.type === 'BUY_X_PAY_Y' ? record.quantity_x + 'x' + record.quantity_y : record.percentage + '%')}</span>;
const DiscountApplicationType = ({ record }) => {
  const translate = useTranslate();
  return <span>{record && translate('tickets_discounts.application_' + record.application_type)}</span>;
};
const DiscountCode = ({ record }) => <span>{record && record.application_type === 'CODE' ? record.discount_code : ''}</span>;

const DiscountDialogButton = ({ record }) => {
  const translate = useTranslate();
  const provider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [fares, setFares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);
  const title = translate('tickets_discounts.' + (record ? 'edit' : 'create'));
  const show = async () => {
    setLoading(true);
    try {
      const associationId = Number(localStorage.getItem('associations_id'));
      const [fareResult, discountResult] = await Promise.all([
        provider.getList('tickets_groups', { pagination: { page: 1, perPage: 1000 }, sort: { field: 'priority', order: 'ASC' }, filter: { associations_id: associationId, no_pagination: true } }),
        record ? provider.getOne('tickets_discounts', { id: record.id }) : Promise.resolve({ data: { associations_id: associationId, name: '', priority: 0, type: 'BUY_X_PAY_Y', application_type: 'FARES', active: true, tickets_groups_ids: [] } })
      ]);
      setFares(fareResult.data);
      setInitial(discountResult.data);
      setTab(0);
      setOpen(true);
    } catch (error) { notify(error.message, 'error'); }
    finally { setLoading(false); }
  };
  const save = async values => {
    setSaving(true);
    const data = { ...values, name: values.name.trim(), tickets_groups_ids: values.application_type === 'FARES' ? (values.tickets_groups_ids || []) : [],
      discount_code: values.application_type === 'CODE' ? values.discount_code.trim() : null,
      quantity_x: values.type === 'BUY_X_PAY_Y' ? values.quantity_x : null,
      quantity_y: values.type === 'BUY_X_PAY_Y' ? values.quantity_y : null,
      percentage: values.type === 'BUY_X_PAY_Y' ? null : values.percentage };
    try {
      if (record) await provider.update('tickets_discounts', { id: record.id, data, previousData: initial });
      else await provider.create('tickets_discounts', { data });
      setOpen(false);
      refresh();
    } catch (error) { notify(error.message, 'error'); }
    finally { setSaving(false); }
  };
  return <>
    <Button label={record ? 'ra.action.edit' : title} onClick={show} disabled={loading}>
      {record ? <EditIcon /> : <AddIcon />}
    </Button>
    <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm" aria-label={title}>
      <DialogTitle>{title}</DialogTitle>
      {open && <FormWithRedirect resource="tickets_discounts" initialValues={initial} validate={validateDiscount} save={save}
        render={({ handleSubmitWithRedirect, pristine }) => <>
          <DialogContent>
            <Tabs value={tab} onChange={(event, value) => setTab(value)} indicatorColor="primary">
              <Tab label={translate('tickets_discounts.data')} />
              <Tab label={translate('tickets_discounts.application')} />
            </Tabs>
            <Divider />
            <Box hidden={tab !== 0} pt={2}>
              <TextInput fullWidth source="name" label={translate('common.name')} />
              <SelectInput fullWidth source="type" label={translate('tickets_discounts.type')}
                choices={types.map(id => ({ id, name: translate('tickets_discounts.' + id) }))} />
              <FormDataConsumer>{({ formData }) => formData.type === 'BUY_X_PAY_Y' ?
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <NumberInput fullWidth source="quantity_x" label={translate('tickets_discounts.quantity_x')} min={1} step={1} />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberInput fullWidth source="quantity_y" label={translate('tickets_discounts.quantity_y')} min={0} step={1} />
                  </Grid>
                </Grid>
                : <NumberInput fullWidth source="percentage" label={translate('tickets_discounts.percentage')} min={0} max={100} step={0.01} />
              }</FormDataConsumer>
            </Box>
            <Box hidden={tab !== 1} pt={2}>
              <RadioButtonGroupInput source="application_type" label={translate('tickets_discounts.application_type')} row={false}
                choices={applicationTypes.map(id => ({ id, name: translate('tickets_discounts.application_' + id) }))} />
              <FormDataConsumer>{({ formData }) => formData.application_type === 'FARES' ? <>
                <Typography variant="body2">{translate('tickets_discounts.fares_help')}</Typography>
                {!fares.length && <Typography>{translate('tickets_discounts.no_fares')}</Typography>}
                <CheckboxGroupInput source="tickets_groups_ids" label={translate('tickets_discounts.fares')} choices={fares} optionText="name" />
              </> : formData.application_type === 'CODE' ?
                <TextInput fullWidth source="discount_code" label={translate('tickets_discounts.discount_code')} inputProps={{ maxLength: 20 }} />
                : null}</FormDataConsumer>
            </Box>
            <Box hidden={tab !== 0} mt={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <NumberInput fullWidth source="priority" label={translate('tickets_discounts.priority')} step={1} />
                </Grid>
                <Grid item xs={6}>
                  <BooleanInput source="active" label={translate('tickets_discounts.active')} />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button label="ra.action.cancel" onClick={() => setOpen(false)} disabled={saving} />
            <SaveButton handleSubmitWithRedirect={event => { setTab(0); handleSubmitWithRedirect(event); }} pristine={pristine} saving={saving} disabled={saving} />
          </DialogActions>
        </>} />}
    </Dialog>
  </>;
};
const Actions = () => <TopToolbar><DiscountDialogButton /></TopToolbar>;
export const SettingsTicketsDiscounts = props => {
  const translate = useTranslate();
  return <>
    <br />
    <Typography variant="h5" className="grey8" align="center">{translate('tickets_discounts.title')}</Typography>
    <Box className="settings_box" p={2}>
      <ResourceContextProvider value="tickets_discounts">
        <List {...props} syncWithLocation={false} basePath="/tickets_discounts" sort={{ field: 'priority', order: 'ASC' }}
          filter={{ associations_id: localStorage.getItem('associations_id') }} actions={<Actions />}
          bulkActionButtons={false} exporter={false} empty={<Actions />} pagination={<Pagination />} perPage={25}>
          <Datagrid>
            <TextField source="name" label={translate('common.name')} sortable={false} />
            <DiscountType label={translate('tickets_discounts.type')} sortable={false} />
            <DiscountValue label={translate('tickets_discounts.value')} sortable={false} />
            <DiscountApplicationType label={translate('tickets_discounts.application')} sortable={false} />
            <DiscountCode label={translate('tickets_discounts.discount_code')} sortable={false} />
            <BooleanField source="active" label={translate('tickets_discounts.active')} sortable={false} />
            <DiscountDialogButton />
            <DeleteButton undoable={false} redirect="/settings-tickets-discounts" label="" />
          </Datagrid>
        </List>
      </ResourceContextProvider>
    </Box>
  </>;
};

